import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import SchedulingCalculator from './features/calculator/SchedulingCalculator';
import ReportViewer from './features/calculator/ReportViewer';
import TasReportViewer from './features/calculator/TasReportViewer';
import AdminDashboard from './features/admin/AdminDashboard';
import roosterLogo from './assets/images/Rooster Logo - Sales and Marketing.png';
import { useVersion } from './contexts/VersionContext';
import './index.css';

function MainApp() {
  const { isRoosterBranded, hasROIAnalysis } = useVersion();
  
  return (
    <div className="bg-gradient-to-br from-gray-50 to-sky-50 min-h-screen py-8 md:py-12">
      <div className="container mx-auto px-4">
        <header className="text-center mb-8 md:mb-12">
          {/* Horizontal Rooster Logo - only for branded versions */}
          {isRoosterBranded && (
            <div className="flex justify-center mb-6 animate-slide-down">
              <img 
                src={roosterLogo} 
                alt="Rooster" 
                className="h-10 md:h-14 w-auto"
              />
            </div>
          )}
          
          {/* Eyebrow Badge */}
          <div className="flex items-center justify-center mb-6 animate-fade-in animate-delay-100">
            <div className="bg-sky-50 border border-sky-200 rounded-full px-4 py-1.5 hover:bg-sky-100 transition-colors">
              <p className="text-xs font-medium text-sky-700 uppercase tracking-wider">
                Performance Benchmark Tool
              </p>
            </div>
          </div>
          
          {/* Main Headline */}
          <h1 className="mb-6 animate-fade-in animate-delay-200">
            <span className="block text-5xl md:text-7xl font-bold tracking-tight bg-gradient-to-r from-sky-700 via-cyan-600 to-sky-500 bg-clip-text text-transparent hover:from-sky-600 hover:to-cyan-400 transition-all duration-300 cursor-default">
              {isRoosterBranded ? "What's Your TAS Score?" : "TAS Score Calculator"}
            </span>
          </h1>
          
          {/* Value-driven Subheadline */}
          <p className="text-xl md:text-2xl text-gray-800 font-normal max-w-2xl mx-auto mb-8 leading-relaxed animate-fade-in animate-delay-300">
            Benchmark your <span className="font-semibold text-sky-700">scheduling efficiency</span> and discover your{' '}
            <span className="font-semibold text-sky-700">improvement potential</span>
          </p>
          
          {/* Quick Benefits */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm animate-fade-in animate-delay-400">
            <div className="flex items-center gap-2 hover:scale-105 transition-transform">
              <svg className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-gray-700">2-minute assessment</span>
            </div>
            <div className="flex items-center gap-2 hover:scale-105 transition-transform">
              <svg className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-gray-700">Actionable insights</span>
            </div>
            {hasROIAnalysis && (
              <div className="flex items-center gap-2 hover:scale-105 transition-transform">
                <svg className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700">Instant ROI report</span>
              </div>
            )}
          </div>
        </header>
        <SchedulingCalculator />
        
        {/* Footer - only for branded versions */}
        {isRoosterBranded && (
          <footer className="mt-16 py-6 border-t border-gray-200">
            <div className="flex flex-col items-center justify-center">
              <span className="text-xs text-gray-500 mb-2">Powered by</span>
              <img 
                src={roosterLogo} 
                alt="Rooster" 
                className="h-8 w-auto opacity-80"
              />
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<MainApp />} />
        <Route path="/tas" element={<MainApp />} />
        <Route path="/generic" element={<MainApp />} />
        <Route path="/rooster-tas" element={<MainApp />} />
        <Route path="/admin" element={<MainApp />} />
        <Route path="/roi" element={<MainApp />} />
        <Route path="/report/:reportId" element={<ReportViewer />} />
        <Route path="/tas-report/:reportId" element={<TasReportViewer />} />
        <Route path="/management" element={<AdminDashboard />} />
      </Routes>
    </Router>
  );
}

export default App;